import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";

import { API_ENDPOINTS } from "../config/constants";

const REPORT_MODE_DAY = "day";
const REPORT_MODE_MONTH = "month";
const DAY_ENDPOINT = API_ENDPOINTS.REPORT_DAY_WISE_ITEMS;
const MONTH_ENDPOINT = API_ENDPOINTS.REPORT_MONTH_WISE_ITEMS;

const buildDefaultReport = ({ mode, date, month }) => ({
  mode,
  date,
  month,
  restaurant: "",
  total_items: 0,
  total_quantity: 0,
  total_amount: 0,
  total_orders: 0,
  items: [],
});

const aggregateCategories = (items = []) => {
  const map = new Map();

  items.forEach((item) => {
    const key = String(item?.category_name || "").trim() || "Uncategorized";
    const current = map.get(key) || {
      category_name: key,
      item_count: 0,
      total_quantity: 0,
      total_orders: 0,
      total_amount: 0,
    };

    current.item_count += 1;
    current.total_quantity += Number(item?.total_quantity || 0);
    current.total_orders += Number(item?.total_orders || 0);
    current.total_amount += Number(item?.total_amount || 0);

    map.set(key, current);
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.total_quantity !== a.total_quantity) {
      return b.total_quantity - a.total_quantity;
    }
    return b.total_amount - a.total_amount;
  });
};

const DayWiseCategoryReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [mode, setMode] = useState(REPORT_MODE_DAY);
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(todayMonth());
  const [report, setReport] = useState(
    buildDefaultReport({ mode: REPORT_MODE_DAY, date: today(), month: todayMonth() })
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode, date, month]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;

    const activeDate = date;
    const activeMonth = month;
    const isMonthMode = mode === REPORT_MODE_MONTH;
    const endpoint = isMonthMode ? MONTH_ENDPOINT : DAY_ENDPOINT;
    const body = isMonthMode
      ? { restaurantid: userId, month: activeMonth }
      : { restaurantid: userId, date: activeDate };

    opts.refresh ? setRefreshing(true) : setLoading(true);

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || "Failed to load report");
      }

      const items = aggregateCategories(Array.isArray(json?.items) ? json.items : []);
      setReport({
        mode,
        date: json?.date || activeDate,
        month: json?.month || activeMonth,
        restaurant: json?.restaurant || "",
        total_items: items.length,
        total_quantity: Number(
          json?.total_quantity ??
            items.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0)
        ),
        total_amount: Number(
          json?.total_amount ??
            items.reduce((sum, item) => sum + Number(item.total_amount || 0), 0)
        ),
        total_orders: Number(
          json?.total_orders ??
            items.reduce((sum, item) => sum + Number(item.total_orders || 0), 0)
        ),
        items,
      });
      setPage(0);
    } catch (error) {
      toast.error(error.message || "Unable to load report");
      setReport(buildDefaultReport({ mode, date: activeDate, month: activeMonth }));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (val) =>
    formatStoredMoney(val, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return { start, end, rows: report.items.slice(start, end) };
  }, [page, report.items]);

  const pageCount = Math.ceil((report.items.length || 0) / rowsPerPage);

  const exportCsv = () => {
    const rows = report.items.map((cat, idx) => [
      idx + 1,
      cat.category_name,
      cat.item_count,
      cat.total_quantity,
      cat.total_orders,
      cat.total_amount,
    ]);
    downloadCSV(
      `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}.csv`,
      ["#", "Category", "Items", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = report.items.map((cat, idx) => [
      idx + 1,
      cat.category_name,
      cat.item_count,
      cat.total_quantity,
      cat.total_orders,
      cat.total_amount,
    ]);
    downloadExcel(
      `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}.xlsx`,
      ["#", "Category", "Items", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportPdf = () =>
    downloadPDF(
      reportRef.current,
      `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}.pdf`
    );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Day-wise Category Report"
        subtitle="Category-wise totals by day or month with aggregated sales."
        stats={[
          { label: "Categories", value: report.total_items || 0 },
          { label: "Total Qty", value: report.total_quantity || 0 },
          { label: "Total Orders", value: report.total_orders || 0 },
          { label: "Total Amount", value: fmtINR(report.total_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker
          mode={mode}
          onModeChange={(m) => setMode(m)}
          date={mode === REPORT_MODE_DAY ? date : undefined}
          onDateChange={(d) => setDate(d)}
          month={mode === REPORT_MODE_MONTH ? month : undefined}
          onMonthChange={(m) => setMonth(m)}
        />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading category data...</Center>
        ) : report.items.length === 0 ? (
          <Center>No category sales found for this selection.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category</th>
                  <th>Item Count</th>
                  <th>Qty Sold</th>
                  <th>Orders</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginated.rows.map((row, idx) => (
                  <tr key={row.category_name || idx}>
                    <td>{paginated.start + idx + 1}</td>
                    <td>
                      <strong>{row.category_name}</strong>
                    </td>
                    <td>{row.item_count}</td>
                    <td>{row.total_quantity}</td>
                    <td>{row.total_orders}</td>
                    <td>{fmtINR(row.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <Pager>
          <PageBtn disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PageBtn>
          <PageInfo>
            Page <strong>{pageCount ? page + 1 : 0}</strong> of <strong>{pageCount}</strong>
          </PageInfo>
          <PageBtn disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </PageBtn>
        </Pager>

        <ActionsRow style={{ marginTop: 14 }}>
          <PrimaryBtn onClick={exportExcel}>Excel</PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn
            onClick={() =>
              printThermal(
                reportRef.current,
                `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}`
              )
            }
          >
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </ActionsRow>
      </Card>
    </Page>
  );
};

export default DayWiseCategoryReportPage;

const today = () => toISO(new Date());
const todayMonth = () => formatMonth(new Date());
const toISO = (d) => toLocalYmd(d);
const formatMonth = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #075f9e, #0786c5);
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(7, 95, 158, 0.25);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 24px 0;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
  tr:hover td {
    background: #f8fafc;
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 7px 14px;
  font-weight: 700;
  color: #075f9e;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: #64748b;
  font-size: 0.88rem;
`;
